import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Cpu, Loader2, Zap, RefreshCw, AlertTriangle, Info, CheckCircle2,
  TrendingUp, ShieldAlert, FileText, Building, FileSignature, Sparkles,
  ChevronRight, Send, Mail, ExternalLink, PlayCircle, Wand2, Bot,
} from 'lucide-react';

// Category icon + color mapping with FULL static Tailwind classes (purge-safe)
const CAT_META = {
  devis_relance: {
    icon: TrendingUp, label: 'Devis à relancer',
    bg: 'bg-amber-100', text: 'text-amber-700',
    chip: 'bg-amber-50 text-amber-700 hover:bg-amber-100',
    chipActive: 'bg-amber-600 text-white',
    btn: 'bg-amber-600 hover:bg-amber-700 text-white',
  },
  doc_expire: {
    icon: ShieldAlert, label: 'Documents à renouveler',
    bg: 'bg-red-100', text: 'text-red-700',
    chip: 'bg-red-50 text-red-700 hover:bg-red-100',
    chipActive: 'bg-red-600 text-white',
    btn: 'bg-red-600 hover:bg-red-700 text-white',
  },
  contract_pending: {
    icon: FileSignature, label: 'Contrats en attente',
    bg: 'bg-purple-100', text: 'text-purple-700',
    chip: 'bg-purple-50 text-purple-700 hover:bg-purple-100',
    chipActive: 'bg-purple-600 text-white',
    btn: 'bg-purple-600 hover:bg-purple-700 text-white',
  },
  brouillon_stale: {
    icon: FileText, label: 'Brouillons dormants',
    bg: 'bg-slate-100', text: 'text-slate-700',
    chip: 'bg-slate-100 text-slate-700 hover:bg-slate-200',
    chipActive: 'bg-slate-700 text-white',
    btn: 'bg-slate-700 hover:bg-slate-800 text-white',
  },
  chantier_no_st: {
    icon: Building, label: 'Chantiers sans artisan',
    bg: 'bg-blue-100', text: 'text-blue-700',
    chip: 'bg-blue-50 text-blue-700 hover:bg-blue-100',
    chipActive: 'bg-blue-600 text-white',
    btn: 'bg-blue-600 hover:bg-blue-700 text-white',
  },
  chorus_alert: {
    icon: AlertTriangle, label: 'Alertes Chorus Pro',
    bg: 'bg-red-100', text: 'text-red-700',
    chip: 'bg-red-50 text-red-700 hover:bg-red-100',
    chipActive: 'bg-red-600 text-white',
    btn: 'bg-red-600 hover:bg-red-700 text-white',
  },
};

const SEVERITY_RING = {
  critical: 'ring-2 ring-red-200 bg-red-50/50',
  warning:  'ring-2 ring-amber-200 bg-amber-50/50',
  info:     'ring-1 ring-slate-200 bg-white',
};
const SEVERITY_BADGE = {
  critical: 'bg-red-100 text-red-700',
  warning:  'bg-amber-100 text-amber-700',
  info:     'bg-slate-100 text-slate-600',
};

const TIP_META = {
  alert:    { icon: AlertTriangle, wrap: 'bg-red-50 border-red-200',         iconWrap: 'bg-red-100',     iconColor: 'text-red-700'     },
  trending: { icon: TrendingUp,    wrap: 'bg-amber-50 border-amber-200',     iconWrap: 'bg-amber-100',   iconColor: 'text-amber-700'   },
  shield:   { icon: ShieldAlert,   wrap: 'bg-red-50 border-red-200',         iconWrap: 'bg-red-100',     iconColor: 'text-red-700'     },
  broom:    { icon: Sparkles,      wrap: 'bg-slate-50 border-slate-200',     iconWrap: 'bg-slate-100',   iconColor: 'text-slate-700'   },
  check:    { icon: CheckCircle2,  wrap: 'bg-emerald-50 border-emerald-200', iconWrap: 'bg-emerald-100', iconColor: 'text-emerald-700' },
};

/**
 * Full-screen orchestrator dashboard — single brain over the whole CRM.
 * Lists every actionable task and lets the user execute them one-by-one
 * or all at once via the "Tout exécuter (auto)" button.
 */
export default function OrchestratorDashboard() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [runningAll, setRunningAll] = useState(false);
  const [runningTask, setRunningTask] = useState({}); // { task_id: action_id }
  const [filter, setFilter] = useState('all');         // 'all' | category key

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/orchestrator/dashboard');
      setData(r.data);
    } catch (_e) {
      toast.error("Erreur lors du chargement de l'orchestrateur");
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const executeAction = async (task, action) => {
    if (action.id.startsWith('open_')) {
      navigate(task.entity_link);
      return;
    }
    setRunningTask((s) => ({ ...s, [task.task_id]: action.id }));
    try {
      const r = await api.post('/orchestrator/execute', {
        task_id: task.task_id,
        action_id: action.id,
      });
      if (r.data.success) {
        toast.success(r.data.message || 'Action exécutée');
        load();
      } else {
        toast.error(r.data.error || "L'action a échoué");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de l'exécution");
    }
    setRunningTask((s) => {
      const next = { ...s };
      delete next[task.task_id];
      return next;
    });
  };

  const runAllAuto = async () => {
    if (!window.confirm("Lancer l'orchestrateur sur toutes les actions auto-exécutables ?\n\nIl enverra les relances clients, les rappels documents et les contrats à signer.")) return;
    setRunningAll(true);
    try {
      const r = await api.post('/orchestrator/run-all-auto');
      toast.success(`Orchestrateur terminé : ${r.data.summary.ok} OK / ${r.data.summary.ko} erreur(s)`);
      load();
    } catch (e) {
      toast.error("Échec de l'exécution massive");
    }
    setRunningAll(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }
  if (!data) return null;

  const tasks = filter === 'all' ? data.tasks : data.tasks.filter((t) => t.category === filter);

  return (
    <div className="space-y-6" data-testid="orchestrator-dashboard">
      {/* ===== Header Hero ===== */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-slate-900 via-blue-900 to-indigo-900 text-white p-6 md:p-8">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(59,130,246,0.15),transparent_70%)]" />
        <div className="relative flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-white/10 backdrop-blur ring-1 ring-white/20 flex items-center justify-center">
              <Cpu className="w-7 h-7" />
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Agent Orchestrateur
              </h1>
              <p className="text-sm text-blue-100 mt-1">
                Un seul cerveau pour tout le CRM · Relances · Documents · Contrats · Chantiers
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="secondary"
              onClick={load}
              disabled={loading}
              className="bg-white/10 backdrop-blur hover:bg-white/20 text-white border border-white/20"
              data-testid="orch-refresh-btn"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Actualiser
            </Button>
            <Button
              onClick={runAllAuto}
              disabled={runningAll || data.counts.auto_runnable === 0}
              className="bg-amber-500 hover:bg-amber-600 text-white font-semibold shadow-lg shadow-amber-900/30"
              data-testid="orch-run-all-btn"
            >
              {runningAll ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Zap className="w-4 h-4 mr-2" />}
              {runningAll ? 'En cours…' : `Tout exécuter (auto) · ${data.counts.auto_runnable}`}
            </Button>
          </div>
        </div>

        {/* Counters */}
        <div className="relative grid grid-cols-2 md:grid-cols-4 gap-3 mt-6">
          <CounterCard label="À traiter" value={data.counts.total} color="white" testid="counter-total" />
          <CounterCard label="Urgent" value={data.counts.critical} color="red" testid="counter-critical" />
          <CounterCard label="À surveiller" value={data.counts.warning} color="amber" testid="counter-warning" />
          <CounterCard label="Auto-exécutables" value={data.counts.auto_runnable} color="emerald" testid="counter-auto" />
        </div>
      </div>

      {/* ===== Tips ===== */}
      {data.tips.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="orch-tips">
          {data.tips.map((tip, i) => {
            const meta = TIP_META[tip.icon] || TIP_META.alert;
            const Icon = meta.icon;
            return (
              <div key={i} className={`p-4 rounded-xl border flex items-start gap-3 ${meta.wrap}`}>
                <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${meta.iconWrap}`}>
                  <Icon className={`w-4 h-4 ${meta.iconColor}`} />
                </div>
                <div className="min-w-0">
                  <p className="font-semibold text-slate-900 text-sm">{tip.title}</p>
                  <p className="text-xs text-slate-600 mt-0.5 leading-snug">{tip.message}</p>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ===== Category filter chips ===== */}
      <div className="flex flex-wrap gap-2" data-testid="orch-filters">
        <button
          onClick={() => setFilter('all')}
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
            filter === 'all' ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
          }`}
        >
          Toutes
          <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${filter === 'all' ? 'bg-white/20' : 'bg-white/70'}`}>
            {data.counts.total}
          </span>
        </button>
        {Object.entries(data.categories).map(([key, cat]) => {
          if (cat.count === 0) return null;
          const meta = CAT_META[key];
          if (!meta) return null;
          const Icon = meta.icon;
          return (
            <button
              key={key}
              onClick={() => setFilter(key)}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                filter === key ? meta.chipActive : meta.chip
              }`}
              data-testid={`orch-filter-${key}`}
            >
              <Icon className="w-3.5 h-3.5" />
              {meta.label}
              <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${filter === key ? 'bg-white/20' : 'bg-white/70'}`}>
                {cat.count}
              </span>
            </button>
          );
        })}
      </div>

      {/* ===== Tasks list ===== */}
      {tasks.length === 0 ? (
        <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center" data-testid="orch-empty">
          <div className="w-16 h-16 rounded-2xl bg-emerald-100 mx-auto flex items-center justify-center mb-4">
            <CheckCircle2 className="w-8 h-8 text-emerald-600" />
          </div>
          <h3 className="text-lg font-bold text-slate-900 mb-1">
            {filter === 'all' ? 'Tout est à jour !' : 'Aucune tâche dans cette catégorie'}
          </h3>
          <p className="text-sm text-slate-500">L'orchestrateur n'a rien à signaler pour le moment.</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="orch-tasks">
          {tasks.map((task) => {
            const meta = CAT_META[task.category] || {
              icon: FileText, label: task.category,
              bg: 'bg-slate-100', text: 'text-slate-700',
              btn: 'bg-slate-700 hover:bg-slate-800 text-white',
            };
            const Icon = meta.icon;
            const running = runningTask[task.task_id];
            return (
              <div
                key={task.task_id}
                className={`rounded-xl border border-slate-200 p-4 md:p-5 transition-all hover:shadow-md ${SEVERITY_RING[task.severity]}`}
                data-testid={`orch-task-${task.task_id}`}
              >
                <div className="flex items-start gap-4">
                  <div className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${meta.bg}`}>
                    <Icon className={`w-5 h-5 ${meta.text}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h4 className="font-bold text-slate-900">{task.title}</h4>
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase ${SEVERITY_BADGE[task.severity]}`}>
                        {task.severity === 'critical' && 'urgent'}
                        {task.severity === 'warning' && 'à traiter'}
                        {task.severity === 'info' && 'info'}
                      </span>
                    </div>
                    <p className="text-sm text-slate-600 mt-1 leading-snug">{task.description}</p>
                    <div className="flex items-center gap-3 mt-3 flex-wrap">
                      {task.actions.map((action) => {
                        const isPrimary = action.primary;
                        const isAuto = action.auto;
                        const isRunning = running === action.id;
                        const isOpen = action.id.startsWith('open_');
                        return (
                          <button
                            key={action.id}
                            onClick={() => executeAction(task, action)}
                            disabled={!!running}
                            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors disabled:opacity-50 ${
                              isPrimary ? meta.btn : 'bg-slate-100 hover:bg-slate-200 text-slate-700'
                            }`}
                            data-testid={`task-action-${task.task_id}-${action.id}`}
                          >
                            {isRunning
                              ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                              : isOpen
                                ? <ExternalLink className="w-3.5 h-3.5" />
                                : isAuto
                                  ? <Wand2 className="w-3.5 h-3.5" />
                                  : <CheckCircle2 className="w-3.5 h-3.5" />}
                            {action.label}
                            {isAuto && !isRunning && !isOpen && (
                              <span className="text-[9px] uppercase font-bold opacity-70">IA</span>
                            )}
                          </button>
                        );
                      })}
                      <button
                        onClick={() => navigate(task.entity_link)}
                        className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-900 ml-auto"
                      >
                        Détail <ChevronRight className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Footer note */}
      <div className="text-center text-xs text-slate-400 pt-2">
        Tableau de bord généré le {new Date(data.generated_at).toLocaleString('fr-FR')} ·
        L'orchestrateur tourne automatiquement toutes les 24 h en arrière-plan
      </div>
    </div>
  );
}

function CounterCard({ label, value, color, testid }) {
  const colorMap = {
    white:   'bg-white/10 text-white border-white/20',
    red:     value > 0 ? 'bg-red-500/20 text-red-100 border-red-400/40' : 'bg-white/5 text-white/60 border-white/10',
    amber:   value > 0 ? 'bg-amber-500/20 text-amber-100 border-amber-400/40' : 'bg-white/5 text-white/60 border-white/10',
    emerald: 'bg-emerald-500/20 text-emerald-100 border-emerald-400/40',
  };
  return (
    <div className={`rounded-xl border backdrop-blur px-4 py-3 ${colorMap[color]}`} data-testid={testid}>
      <p className="text-2xl font-bold">{value}</p>
      <p className="text-[11px] uppercase tracking-wide opacity-80 mt-0.5">{label}</p>
    </div>
  );
}
